/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import {
  UserInterface,
  UserRolesEnum,
  PersonInterface,
  PatientInterface,
  TemplateTypeEnum,
  TemplateContentTypeEnum,
  ProviderInterface,
  AppointmentInterface,
  VisitTypeEnum,
  VisitStatusEnum,
  VisitOfficeStatusEnum,
  VitalsInterface,
  ProgressNoteInterface,
  PharmacyInterface,
  ResourceInterface,
  FacilityInterface,
  FacilityTypeEnum,
  InsuranceInterface,
  PatientInsuranceInterface,
  CaseInterface,
  BillingEncounterInterface,
  PayerInterface,
  PricingInterface,
  InsuranceTransactionInterface,
  AccountingInterface,
  PaymentInterface,
  TransactionStatusEnum
} from "medicalsystem-shared";
import { getFromISOYYYYMMDDDateFormat } from "./date.utils";
import { ExtendedPersonInterface } from "interfaces/parent.person.interface";
import { StatusCaseEnum, TypeCaseEnum } from "medicalsystem-shared/dist/enums/case.enum";

//TODO: This imports hasn't been added to official version of node_modules yet, need to add it later
import {
  TaxType,
  PayerTypeEnum,
  PaymentMethodEnum,
  CurrencyEnum,
  AccountingStatusEnum,
} from "medicalsystem-shared";
import { NO_INSURANCE_OPTION_ID } from "constants/insurance.const";

/**
 * Creates a new template data object with default values
 * @returns A template object with default title, empty content, and default type
 */
export const getNewTemplateDataContent = () => {
  return {
    title: "Default",
    template: "<p></p>",
    type: TemplateTypeEnum.HPI || TemplateTypeEnum.PRESCRIPTION,
    version: "",
    contentType: TemplateContentTypeEnum.TEXT_HTML,
    tags: [],
  };
};
/**
 * Creates a new user data object with empty default values
 * @returns A user object with empty username, email, roles, and password
 */
export const getNewUserDataContent = () => {
  return {
    username: " ",
    email: " ",
    roles: [],
    auth: {
      password: "",
    },
  };
};
/**
 * Creates a new prescription template data object with default values
 * @returns A prescription template object with default settings
 */
export const getNewPrescriptionTemplateDataContent = () => {
  return {
    title: "Default",
    template: "<p></p>",
    type: TemplateTypeEnum.PRESCRIPTION,
    version: "",
    contentType: TemplateContentTypeEnum.TEXT_HTML,
    tags: [],
  };
};

/**
 * Creates a "No Insurance" placeholder option for patient insurance list
 * @param patient - Optional patient to associate with the placeholder
 * @returns A PatientInsuranceInterface object representing no insurance
 */
export const createNoInsuranceOption = (patient?: Partial<PatientInterface> | null): PatientInsuranceInterface => {
  return {
    patient: patient || null,
    insurance: {
      id: NO_INSURANCE_OPTION_ID,
      name: NO_INSURANCE_OPTION_ID,
    } as InsuranceInterface,
    policyId: "N/A",
    coPay: 0,
    coInsurance: 0,
    coverageLimit: 0,
  };
};

/**
 * Creates a new patient data object with default values and empty fields
 * @returns A PatientInterface object with default person and patient information
 */
export const getNewPatientDataContent = (): PatientInterface => {
  return {
    person: {
      name: "",
      lastname: "",
      user: {
        roles: [UserRolesEnum.PATIENT],
        username: "",
        email: "",
        auth: {
          password: "",
        },
      },
      phones: [],
      sex: "",
      dob: getFromISOYYYYMMDDDateFormat(new Date()),
      address: "",
      city: "",
      zip: "",
      state: "",
      county: "",
      country: "",
      identity: "",
      emergencyContacts: [],
    },
    historyConsent: false,
    releaseInformation: false,
    accountNumber: 0,
    ssn: "",
    referringProvider: {},
    defaultPharmacy: {},
  };
};

/**
 * Creates a new HPI (History of Present Illness) template data object
 * @returns An HPI template object with default settings
 */
export const getNewHPITemplateDataContent = () => {
  return {
    title: "Default",
    template: "<p></p>",
    type: TemplateTypeEnum.HPI,
    version: "",
    contentType: TemplateContentTypeEnum.TEXT_HTML,
    tags: [],
  };
};

/**
 * Creates a new billing encounter data object with default empty values
 * @returns A BillingEncounterInterface object with empty appointment, pricing, and zero amounts
 */
export const getNewBillingEncounterDataContent = (): BillingEncounterInterface => {
  return {
    id: "",
    appointment: { id: "" },
    pricing: { id: "" },
    price: 0,
    discount: 0,
    pricingName: "",
    pricingDescription: "",
  };
};

/**
 * Creates a new doctor/provider data object with default values
 * @returns A ProviderInterface object with empty person information and doctor-specific fields
 */
export const getNewDoctorDataContent = (): ProviderInterface => {
  return {
    person: {
      name: "",
      lastname: "",
      user: {
        roles: [UserRolesEnum.DOCTOR],
        username: "",
        email: "",
        auth: {
          password: "",
        },
      },
      phones: [],
      sex: "",
      dob: getFromISOYYYYMMDDDateFormat(new Date()),
      address: "",
      city: "",
      zip: "",
      state: "",
      county: "",
      country: "",
      identity: "",
      emergencyContacts: [],
    },
    medicalLicense: "",
    specialty: "",
    deaRegistration: "",
  };
};

/**
 * Creates a new nurse data object with default values
 * @returns A provider object with NURSE role and empty person information
 */
export const getNewNurseDataContent = () => {
  return {
    person: {
      name: "",
      lastname: "",
      user: {
        roles: [UserRolesEnum.NURSE],
        username: "",
        email: "",
        auth: {
          password: "",
        },
      },
      phones: [],
      sex: "",
      dob: getFromISOYYYYMMDDDateFormat(new Date()),
      address: "",
      city: "",
      zip: "",
      state: "",
      county: "",
      country: "",
      identity: "",
      emergencyContacts: [],
    },
    medicalLicense: "",
    specialty: "",
    deaRegistration: "",
  };
};

/**
 * Creates a new appointment object with default values
 * @param date - Optional date for the appointment; if not provided, uses current date
 * @returns An AppointmentInterface object with default visit type, status, and empty fields
 */
export const getNewAppointment = (date?: Date): AppointmentInterface => {
  let newDate = date;
  if (!newDate) {
    newDate = new Date();
  }
  return {
    reason: "",
    arrivalDateTime: date,
    duration: 15,
    provider: {},
    patient: {},
    facility: {},
    roomNumber: "",
    visitType: VisitTypeEnum.FOLLOW_UP,
    visitStatus: VisitStatusEnum.CHECK_IN,
    visitOfficeStatus: VisitOfficeStatusEnum.READY,
    resource: {},
  };
};

/**
 * Checks if an object can be cast to a specific type
 * @template T - The target type to check against
 * @param model - The object to check
 * @returns True if the model can be cast to type T, false otherwise
 */
export const isIstanceOf = <T extends Object>(model: Object) => {
  let result: boolean = false;

  try {
    let newModel: T = model as T;
    result = true;
  } catch (e) {}

  return result;
};

/**
 * Creates or validates a user model with default values for missing fields
 * @param user - Optional partial user object to validate
 * @param roles - Optional array of user roles to assign
 * @returns A complete UserInterface object with all required fields
 */
export const getValidUserModel = (
  user?: Partial<UserInterface>,
  roles?: UserRolesEnum[]
): UserInterface => {
  let newUser: Partial<UserInterface> = user;

  if (!newUser) {
    newUser = {
      username: "",
      email: "",
      auth: {
        password: "",
      },
      roles: [],
    };
  } else {
    newUser.auth = newUser.auth ? newUser.auth : { password: "", isActive: true };
    newUser.email = newUser.email ? newUser.email : "";
    newUser.roles = newUser.roles ? newUser.roles : [];
    newUser.username = newUser.username ? newUser.username : "";
  }

  if (roles) {
    newUser.roles = roles;
  }

  return newUser as UserInterface;
};

/**
 * Creates or validates a person model with default values for missing fields
 * @param person - Optional partial person object to validate
 * @param roles - Optional array of user roles to assign to the person's user
 * @returns A partial PersonInterface object with validated fields
 */
export const getValidPersonModel = (
  person?: Partial<PersonInterface>,
  roles?: UserRolesEnum[]
): Partial<PersonInterface> => {
  let newPerson: Partial<PersonInterface> = person;

  if (!newPerson) {
    newPerson = {
      name: "",
      lastname: "",
      user: {
        roles: [UserRolesEnum.PATIENT],
        username: "",
        email: "",
        auth: {
          password: "",
        },
      },
      phones: [],
      sex: "",
      prefix: null,
      suffix: null,
      dob: getFromISOYYYYMMDDDateFormat(new Date()),
      address: "",
      city: "",
      zip: "",
      state: "",
      county: "",
      country: "",
      emergencyContacts: [],
    };
  }

  if (newPerson.user) {
    newPerson.user.roles = roles;
  }

  return newPerson;
};
/**
 * Creates or validates a complete person model for user association
 * @param person - Optional person object to validate
 * @param roles - Optional array of user roles to assign
 * @returns A complete PersonInterface object with all required fields
 */
export const getValidPersonModelForUser = (
  person?: PersonInterface,
  roles?: UserRolesEnum[]
): PersonInterface => {
  let newPerson: PersonInterface = person;

  if (!newPerson) {
    newPerson = {
      name: "",
      lastname: "",
      user: {
        roles: [UserRolesEnum.PATIENT],
        username: "",
        email: "",
        auth: {
          password: "",
        },
      },
      phones: [],
      sex: "",
      prefix: null,
      suffix: null,
      dob: getFromISOYYYYMMDDDateFormat(new Date()),
      address: "",
      city: "",
      zip: "",
      state: "",
      county: "",
      country: "",
      emergencyContacts: [],
    };
  }

  if (newPerson.user) {
    newPerson.user.roles = roles;
  }

  return newPerson;
};
/**
 * Creates or validates a patient model with default values for missing fields
 * @param patient - Partial patient object to validate
 * @returns A complete PatientInterface object with all required fields
 */
export const getValidPatientModel = (patient: Partial<PatientInterface>): PatientInterface => {
  let newPatient = patient;

  if (!newPatient) {
    newPatient = {
      historyConsent: false,
      releaseInformation: false,
      accountNumber: 0,
      ssn: "",
    };
  } else {
    newPatient.historyConsent = newPatient.historyConsent ? newPatient.historyConsent : false;
    newPatient.releaseInformation = newPatient.releaseInformation
      ? newPatient.releaseInformation
      : false;
    newPatient.accountNumber = newPatient.accountNumber ? newPatient.accountNumber : 0;
    newPatient.ssn = newPatient.ssn ? newPatient.ssn : "";
  }
  return newPatient as PatientInterface;
};

/**
 * Creates a new vitals data object with default values
 * @returns A VitalsInterface object with current date and empty fields
 */
export const getNewVitalsDataContent = (): VitalsInterface => {
  return {
    registrationDate: new Date(),
    note: "",
    painScale: 0,
    appointment: {},
  };
};

/**
 * Creates a new progress note data object with default values
 * @returns A ProgressNoteInterface object with current date and empty fields
 */
export const getNewProgressNoteDataContent = (): ProgressNoteInterface => {
  return {
    registrationDate: new Date(),
    appointment: {},
    chiefComplaints: "",
    hpi: "",
    hpiTitle: "",
    hpiVersion: "",
    hpiContentType: TemplateContentTypeEnum.TEXT_HTML,
    comments: "",
  };
};

/**
 * Creates a new parent/guardian data object with empty fields
 * @returns An ExtendedPersonInterface object for parent/guardian information
 */
export const getNewParentDataContent = (): ExtendedPersonInterface => {
  return {
    name: "",
    lastname: "",
    phones: [],
    phone: "",
    username: "",
    email: "",
    dob: "",
    sex: "",
    identity: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    relationship: "",
    other: "",
    comment: "",
    idlocal: "",
  };
};

/**
 * Creates a new pharmacy data object with default values
 * @returns A PharmacyInterface object with empty fields and boolean flags set to false
 */
export const getNewPharmacyDataContent = (): PharmacyInterface => {
  return {
    displayName: "",
    storeName: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    county: "",
    mailOrderPharmacy: false,
    ePrescribe: false,
    phone: "",
    fax: "",
  };
};

/**
 * Creates a new insurance company data object with empty fields
 * @returns An InsuranceInterface object with default empty values
 */
export const getNewInsuranceDataContent = (): InsuranceInterface => {
  return {
    name: "",
    typeScheme: "",
    payerId: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    termsPayment: "",
    requirements: "",
  };
};

/**
 * Creates a new resource data object with empty fields
 * @returns A ResourceInterface object with default empty values
 */
export const getNewResourceDataContent = (): ResourceInterface => {
  return {
    name: "",
    code: "",
    location: {},
    defaultAppointmentProvider: {},
    phone: "",
  };
};

/**
 * Creates a new facility data object with default values
 * @returns A FacilityInterface object with default type GROUP and current start date
 */
export const getNewFacilityDataContent = (): FacilityInterface => {
  return {
    name: "",
    type: FacilityTypeEnum.GROUP,
    code: "",
    primaryPracticeFlag: false,
    address: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    phone: "",
    fax: "",
    email: "",
    notes: "",
    startDate: new Date(),
  };
};

/**
 * Creates a new case data object with default values
 * @returns A CaseInterface object with default type GENERAL_CONSULTATION and status ACTIVE
 */
export const getNewCaseDataContent = (): CaseInterface => {
  return {
    label: "",
    comment: "",
    patient: {},
    provider: {},
    typeCase: TypeCaseEnum.GENERAL_CONSULTATION,
    status: StatusCaseEnum.ACTIVE,
  };
};
/**
 * Creates a new payer data object with empty fields
 * @returns A PayerInterface object with default empty values
 */
export const getNewPayerDataContent = (): PayerInterface => {
  return {
    id: "",
    name: "",
    lastname: "",
    phone: "",
    state: "",
  };
};
/**
 * Creates a new pricing data object with default values
 * @returns A PricingInterface object with zero prices and default tax types
 */
export const getNewPricingDataContent = (): PricingInterface => {
  return {
    id: "",
    name: "",
    description: "",
    price: 0,
    finalPrice: 0,
    taxAmountType: TaxType.OTHER,
    taxAmount: 0,
    alternativeTaxAmountType: TaxType.OTHER,
    alternativeTaxAmount: 0,
  };
};
/**
 * Creates a new accounting data object with default values
 * @returns An AccountingInterface object with current dates and OPEN status
 */
export const getNewAccountingDataContent = (): AccountingInterface => {
  return {
    id: "",
    patient: {
      id: "",
    },
    startingDate: new Date(),
    closingDate: new Date(),
    status: AccountingStatusEnum.OPEN,
  };
};

/**
 * Creates a new insurance transaction data object with default values
 * @returns An InsuranceTransactionInterface object with PENDING status and zero amounts
 */
export const getNewInsuranceTransactionDataContent = (): InsuranceTransactionInterface => {
  return {
    id:"",
    payment:{},
    patientInsurance: {}, 
    folio: '', 
    copayAmount: 0, 
    totalAmount: 0, 
    claim: 0, 
    status:TransactionStatusEnum.PENDING
  };
};

/**
 * Creates a new payment data object with default values
 * @returns A PaymentInterface object with PATIENT payer type, CASH method, and USD currency
 */
export const getNewPaymentDataContent = (): PaymentInterface => {
  return {
    id: "",
    folio: "",
    payerType: PayerTypeEnum.PATIENT,
    payer: {
      id: "",
      name: "",
    },
    paymentMethod: PaymentMethodEnum.CASH,
    posted: 0,
    currency: CurrencyEnum.USD,
    exchangeRate: { rate: 1 },
    equivalentPosted: 0,
  };
};

/**
 * Creates a new billing encounter values object with empty fields
 * @returns A BillingEncounterInterface object with zero amounts and empty references
 */
export function getNewBillingEncounterValues(): BillingEncounterInterface {
  return {
    id: "",
    price: 0,
    discount: 0,
    pricingName: "",
    pricingDescription: "",
    appointment: {},
    pricing: {},
  };
}

/**
 * Creates a new patient accounting values object for a specific patient
 * @param patientId - The unique identifier of the patient
 * @returns A partial AccountingInterface object with patient reference and OPEN status
 */
export function getNewPatientAccountingValues(patientId: string): Partial<AccountingInterface> {
  return {
    patient: { id: patientId },
    startingDate: new Date(),
    closingDate: new Date(),
    status: AccountingStatusEnum.OPEN,
  };
}

